import {
	Box,
	Button,
	Checkbox,
	Container,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	Hidden,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import { generateClient } from "aws-amplify/api";
import ResidentDataGrid, { PageInfo } from "./ResidentsDataGrid";
import { communities } from "../../shared/Constants";
import * as queries from "../../../graphql/queries";
import log from "loglevel";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { GridPaginationModel } from "@mui/x-data-grid";
import {
	ModelResidentFilterInput,
	Resident,
	SearchResidentsQuery,
	SearchableResidentFilterInput,
	UpdateResidentInput,
} from "../../../API";
import GetAppIcon from "@mui/icons-material/GetApp";
import EventService from "../../../EventService";
import { useParams } from "react-router-dom";
import ExportButton from "./ExportButton";
import ResidentExportButton from "./ExportButton";
import buildFilter from "./Filters/FilterUtils";

const PAGE_SIZE = 25;
const client = generateClient();

const ResidentExportDirectory = () => {
	const [residents, setResidents] = useState<Array<Resident | null>>([]);
	const [selectedCommunity, setSelectedCommunity] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>();
	const [searchKey, setSearchKey] = useState<string>();
	const [selectedLevelOfCare, setSelectedLevelOfCare] = useState("");
	const [initialUserInfo, setInitialUserInfo] = useState<UpdateResidentInput>();
	const [userInfo, setUserInfo] = useState<UpdateResidentInput>();
	// keep track of pagination token for different pages of data
	const nextTokenCache = React.useRef<{ [key: number]: string | null }>({});

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: PAGE_SIZE,
	});

	const onPaginationModelChange = (newPaginationModel: GridPaginationModel) => {
		console.log("1");
		setPaginationModel(newPaginationModel);
		// retrives the pagination token for the next page of data based on the current page number
		const nextPageToken =
			nextTokenCache.current[newPaginationModel.page] ?? null;
		return loadResidentsSearch(
			selectedCommunity,
			selectedLevelOfCare,
			searchKey,
			nextPageToken,
			newPaginationModel.page
		);
	};

	const loadResidentsSearch = React.useCallback(
		async (
			community: string | undefined,
			activeLevelOfCare: string | undefined,
			searchKey: string | undefined,
			nextToken: string | null,
			currentPage: number
		) => {
			setIsLoading(true);
			console.log("2");
			try {
				const filter = buildFilter(community, activeLevelOfCare, searchKey);

				const hasFilterConditions = !!filter.and?.length;
				const filterObject = hasFilterConditions ? { filter } : {};
				log.debug("Query filter", filterObject);

				console.log("Next token before API call: ", nextToken);

				const resp = (await client.graphql({
					query: queries.searchResidents,
					variables: {
						...filterObject,
						nextToken,
						limit: PAGE_SIZE,
					},
				})) as {
					data: SearchResidentsQuery;
				};

				// Store the nextToken for the next page in the cache.
				nextTokenCache.current[currentPage + 1] =
					resp.data.searchResidents?.nextToken || null;

				console.log(
					"Next token after API call: ",
					resp.data.searchResidents?.nextToken
				);

				const newResidents = resp.data.searchResidents?.items || [];

				setIsLoading(false);
				log.debug(`Found ${newResidents.length} Resident records`);

				setResidents(newResidents);

				// If there's no nextToken (indicating no more pages), return PageInfo.
				if (resp.data.searchResidents?.nextToken === null) {
					return {
						totalRowCount:
							(paginationModel.page + 1) * PAGE_SIZE + newResidents.length,
					} as PageInfo;
				}
			} catch (error) {
				log.error(error);
				setIsLoading(false);
				setResidents([]);
			}

			return {} as PageInfo;
		},
		[buildFilter, paginationModel.page]
	);

	useEffect(() => {
		console.log("3");
		if (selectedCommunity) {
			loadResidentsSearch(
				selectedCommunity,
				selectedLevelOfCare,
				undefined,
				null,
				0
			);
		}
	}, [selectedCommunity]);

	useEffect(() => {
		console.log("4");
		if (debouncedSearchKey !== undefined) {
			setPaginationModel({ page: 0, pageSize: PAGE_SIZE });
			nextTokenCache.current = {};
			loadResidentsSearch(
				selectedCommunity,
				selectedLevelOfCare,
				debouncedSearchKey,
				null,
				0
			);
		}
	}, [debouncedSearchKey, loadResidentsSearch, selectedCommunity]);

	useEffect(() => {
		console.log("4");
		// Setup debounce
		const timerId = setTimeout(() => {
			setDebouncedSearchKey(searchKey);
		}, 500);

		// Cleanup
		return () => {
			clearTimeout(timerId);
		};
	}, [searchKey]);

	const onSearchKeyChanged = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const newSearchKey = event.target.value;
		setSearchKey(newSearchKey);
	};

	const onCommunityChange = async (event: SelectChangeEvent<string>) => {
		console.log("6");
		const newCommunity: string = event.target.value;
		setSelectedCommunity(newCommunity);
		setPaginationModel({ page: 0, pageSize: PAGE_SIZE });
		nextTokenCache.current = {};
		await loadResidentsSearch(newCommunity, undefined, searchKey, null, 0);
	};

	const onLevelOfCareChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const activeLevelOfCare: string = event.target.value;
		const checked: boolean = event.target.checked;

		setSelectedLevelOfCare(activeLevelOfCare);
		setPaginationModel({ page: 0, pageSize: PAGE_SIZE });
		nextTokenCache.current = {};
		if (checked) {
			await loadResidentsSearch(
				selectedCommunity,
				activeLevelOfCare,
				undefined,
				null,
				0
			);
		} else {
			setResidents([]);
		}
	};

	return (
		<Container>
			<Box my={3}>
				<Typography variant="h5" component="h2" fontWeight="bold">
					Resident Report
				</Typography>
			</Box>

			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				spacing={3}
				style={{ marginBottom: "10px" }}
			>
				<Grid item xs={12} sm={6} md={3}>
					<FormControl fullWidth variant="outlined" size="small">
						<InputLabel htmlFor="community">Community</InputLabel>
						<Select
							label="Community"
							value={selectedCommunity}
							onChange={onCommunityChange}
							inputProps={{
								name: "community",
								id: "community",
							}}
						>
							{communities.map((c: string) => (
								<MenuItem value={c} key={c}>
									{c}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid>
					<FormGroup
						sx={{
							"&.MuiFormGroup-root": {
								flexDirection: "row",
								flexWrap: "nowrap",
							},
						}}
					>
						<Grid item xs={4}>
							<FormControlLabel
								control={
									<Checkbox value="SNF" onChange={onLevelOfCareChange} />
								}
								label="SNF"
							/>
						</Grid>
						<FormControlLabel
							control={<Checkbox value="MEM" onChange={onLevelOfCareChange} />}
							label="MEM"
						/>
						<Grid item xs={4}>
							<FormControlLabel
								control={
									<Checkbox
										value="IND"
										onChange={onLevelOfCareChange}
										// checked={selectedLevelOfCare === "IND"}
									/>
								}
								label="IL"
							/>
						</Grid>
						<Grid item xs={4}>
							<FormControlLabel
								control={
									<Checkbox value="ALF" onChange={onLevelOfCareChange} />
								}
								label="ALF"
							/>
						</Grid>
					</FormGroup>
				</Grid>
				<Hidden mdDown>
					<Grid item md={4} />
				</Hidden>
				<Grid item xs={12} sm={6} md={5}>
					<TextField
						size="small"
						label="Search"
						fullWidth
						onChange={onSearchKeyChanged}
					/>
				</Grid>
			</Grid>
			<Grid item xs={12} sm={6} md={5}>
				<ResidentExportButton
					selectedCommunity={selectedCommunity}
					selectedLevelOfCare={selectedLevelOfCare}
					searchKey={debouncedSearchKey}
					initialUserInfo={initialUserInfo}
					userInfo={userInfo}
				/>
			</Grid>

			<div style={{ minHeight: 5, width: "100%" }}>
				{isLoading && <LinearProgress />}
			</div>

			<Box>
				<ResidentDataGrid
					key={debouncedSearchKey}
					residents={residents}
					pageSize={PAGE_SIZE}
					onPaginationModelChange={onPaginationModelChange}
					isLoading={isLoading}
				/>
			</Box>
		</Container>
	);
};

export default ResidentExportDirectory;
